/**
 * Deleting a Business Discovery from the console.
 *
 * Kept free of React and of the API client so the rules can be tested with
 * `node --test`: the confirmation, the request and what happens to the list
 * are all passed in.
 *
 * The backend removes the session's PostgreSQL rows and MinIO objects. It is
 * the only copy — nothing is kept in the browser — so a deletion that did not
 * happen must leave the session on screen, where it can be tried again.
 */

/** The question put to the operator before anything is deleted. */
export function confirmationMessage(company, sessionId) {
  const name = company ? `${company} (${sessionId})` : sessionId;
  return (
    `Delete the Business Discovery for ${name}?\n\n` +
    'This permanently removes the session, its transcript, discovery, report, ' +
    'documents and recording. It cannot be undone.'
  );
}

/** The list without one session. */
export function withoutSession(sessions, sessionId) {
  return sessions.filter((session) => session.session_id !== sessionId);
}

/**
 * Ask, delete, and report the outcome.
 *
 * Returns one of:
 *   { outcome: 'cancelled' }                 nothing was sent
 *   { outcome: 'deleted' }                   gone; remove it from the list
 *   { outcome: 'already_gone' }              404 — no longer there; remove it too
 *   { outcome: 'failed', message }           still there; keep it and say why
 */
export async function deleteWithConfirmation({ sessionId, company, confirm, remove }) {
  if (!confirm(confirmationMessage(company, sessionId))) {
    return { outcome: 'cancelled' };
  }
  try {
    await remove(sessionId);
    return { outcome: 'deleted' };
  } catch (cause) {
    if (cause?.status === 404) return { outcome: 'already_gone' };
    const detail = cause?.status ? cause.message : 'The server could not be reached.';
    return {
      outcome: 'failed',
      message: `The Business Discovery ${sessionId} was not deleted. ${detail || ''}`.trim(),
    };
  }
}
